import * as math from './math';
import { compute } from './compute';

/**
 * Calculate the slope for a specific field in an array of data
 * 
 * @param bars Array of objects
 * @param sourceField Field to calculate slope on
 * @param targetField Field name to store the slope value
 * @param period Number of bars to use for calculation (default: 10)
 * @returns Array of objects with slope values in the specified target field
 */
export const slope = <T, K extends string>(
    bars: T[],
    sourceField: keyof T,
    targetField: K,
    period: number = 10
): Array<T & { [P in K]: number | undefined }> => {
    if (bars.length === 0) {
        return [];
    }

    return compute(
        bars,
        targetField,
        (bar, index, allBars, params) => {
            if (index < params.period - 1) {
                return undefined;
            }

            // Get the values for the specified field from the last 'period' bars
            const values = [];
            for (let i = index - params.period + 1; i <= index; i++) {
                values.push(Number(allBars[i][params.sourceField]));
            }

            // Calculate linear regression
            let sumX = 0;
            let sumY = 0;
            let sumXY = 0;
            let sumXX = 0;
            
            for (let i = 0; i < params.period; i++) {
                sumX += i;
                sumY += values[i];
                sumXY += i * values[i];
                sumXX += i * i;
            }
            
            // Calculate slope using linear regression formula
            return (params.period * sumXY - sumX * sumY) / (params.period * sumXX - sumX * sumX);
        },
        { period, sourceField }
    );
};

/**
 * Compares the relative change between consecutive bars for a specific field
 * 
 * @param bars Array of data
 * @param sourceField Field to compute relative change for
 * @param targetField Field name to store the result
 * @returns Array of bars with added relative change field
 */
export function relativeChange<T, K extends string>(
    bars: T[],
    sourceField: keyof T, 
    targetField: K
): Array<T & { [P in K]: number }> {
    return compute(bars, targetField, (bar, index, allBars) => {
        if (index === 0) return 0;
        const prevBar = allBars[index - 1];
        const currentValue = Number(bar[sourceField]);
        const previousValue = Number(prevBar[sourceField]);
        
        if (previousValue === 0) {
            return 0; // Avoid division by zero
        }
        
        return (currentValue - previousValue) / previousValue;
    });
}

/**
 * Calculate relative value compared to historical average
 * @param bars Array of bars
 * @param field Field to calculate relative value for
 * @returns Relative value as a ratio
 */
export function relativeValue<T>(bars: T[], field: keyof T): number {
    if (bars.length === 0) return 0;
    
    const currentBar = bars[bars.length - 1];
    const previousBars = bars.slice(0, bars.length - 1);
    const averageHistoricalValue = math.average(previousBars, field);

    if (averageHistoricalValue === 0) {
        return 0; // Avoid division by zero
    }
    return (currentBar[field] as any as number) / averageHistoricalValue;
}

/**
 * Detects crossover between two arrays at their last values
 * @param barsA First array
 * @param barsB Second array  
 * @param field Field to compare
 * @returns True if A crosses over B
 */
export const crossover = <T>(barsA: T[], barsB: T[], field: keyof T): boolean => {
    const index = barsA.length - 1;
    if (index < 1) return false;
    
    return math.crossoverBase(
        barsA[index - 1][field] as any,
        barsA[index][field] as any,
        barsB[index - 1][field] as any,
        barsB[index][field] as any
    );
}

/**
 * Detects crossunder between two arrays at their last values
 * @param barsA First array
 * @param barsB Second array
 * @param field Field to compare  
 * @returns True if A crosses under B
 */
export const crossunder = <T>(barsA: T[], barsB: T[], field: keyof T): boolean => {
    const index = barsA.length - 1;
    if (index < 1) return false;
    
    return math.crossunderBase(
        barsA[index - 1][field] as any,
        barsA[index][field] as any,
        barsB[index - 1][field] as any,
        barsB[index][field] as any
    );
}

/**
 * Detects crossover between two number arrays
 * @param valuesA First array of numbers
 * @param valuesB Second array of numbers
 * @param index Optional index to check (defaults to last)
 * @returns True if A crosses over B
 */
export const crossoverValues = (valuesA: number[], valuesB: number[], index?: number): boolean => {
    const idx = index !== undefined ? index : valuesA.length - 1;
    return idx > 0 && math.crossoverBase(
        valuesA[idx - 1],
        valuesA[idx],
        valuesB[idx - 1],
        valuesB[idx]
    );
}

/**
 * Detects crossunder between two number arrays
 * @param valuesA First array of numbers
 * @param valuesB Second array of numbers
 * @param index Optional index to check (defaults to last)
 * @returns True if A crosses under B
 */
export const crossunderValues = (valuesA: number[], valuesB: number[], index?: number): boolean => {
    const idx = index !== undefined ? index : valuesA.length - 1;
    return idx > 0 && math.crossunderBase(
        valuesA[idx - 1],
        valuesA[idx],
        valuesB[idx - 1],
        valuesB[idx]
    );
}

/**
 * Calculates z-score for the last value in an array
 * @param bars Array of bars
 * @param field Field to calculate z-score for
 * @returns Z-score of the last value
 */
export const zscore = <T>(bars: T[], field: keyof T): number => {
    const values = bars.map((bar) => Number(bar[field]));

    const mean = values.reduce((sum, num) => sum + num, 0) / values.length;
    const variance = values.reduce((sum, num) => sum + Math.pow(num - mean, 2), 0) / values.length;
    const standardDeviation = Math.sqrt(variance);

    if (standardDeviation === 0) {
        return 0;
    }

    const currentValue = Number(bars[bars.length - 1][field]);
    return (currentValue - mean) / standardDeviation;
}